import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  project_type: "achat",
  message: "",
};

export default function QuoteDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quote`, form);
      toast.success(
        res.data?.message || "Demande reçue. Nous vous répondons sous 24h."
      );
      setForm(initial);
      onOpenChange(false);
    } catch (e) {
      toast.error("Erreur lors de l'envoi. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-[#0E0E12] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-outfit text-2xl md:text-3xl font-light">
            Demander un <span className="gradient-text font-medium">devis</span>
          </DialogTitle>
          <DialogDescription className="text-white/55">
            Parlez-nous de votre projet. Réponse sous 24h.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-white/70 text-xs uppercase tracking-wider">
                Nom *
              </Label>
              <Input
                id="name"
                data-testid="quote-input-name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                className="mt-2 bg-black/30 border-white/10 text-white focus:border-[#E52431]/60"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-wider">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="quote-input-email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                required
                className="mt-2 bg-black/30 border-white/10 text-white focus:border-[#E52431]/60"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone" className="text-white/70 text-xs uppercase tracking-wider">
                Téléphone
              </Label>
              <Input
                id="phone"
                data-testid="quote-input-phone"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="mt-2 bg-black/30 border-white/10 text-white focus:border-[#E52431]/60"
              />
            </div>
            <div>
              <Label htmlFor="company" className="text-white/70 text-xs uppercase tracking-wider">
                Entreprise
              </Label>
              <Input
                id="company"
                data-testid="quote-input-company"
                value={form.company}
                onChange={(e) => handleChange("company", e.target.value)}
                className="mt-2 bg-black/30 border-white/10 text-white focus:border-[#E52431]/60"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/70 text-xs uppercase tracking-wider">
              Type de projet *
            </Label>
            <Select
              value={form.project_type}
              onValueChange={(v) => handleChange("project_type", v)}
            >
              <SelectTrigger
                data-testid="quote-select-type"
                className="mt-2 bg-black/30 border-white/10 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0E0E12] border-white/10 text-white">
                <SelectItem value="achat">Achat de site</SelectItem>
                <SelectItem value="paiement_plusieurs_fois">
                  Paiement en plusieurs fois
                </SelectItem>
                <SelectItem value="location">Location mensuelle</SelectItem>
                <SelectItem value="audit">Audit de site (190 € TTC)</SelectItem>
                <SelectItem value="branding">Branding / Visuels</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="message" className="text-white/70 text-xs uppercase tracking-wider">
              Décrivez votre projet *
            </Label>
            <Textarea
              id="message"
              data-testid="quote-input-message"
              rows={5}
              value={form.message}
              onChange={(e) => handleChange("message", e.target.value)}
              required
              className="mt-2 bg-black/30 border-white/10 text-white focus:border-[#E52431]/60 resize-none"
            />
          </div>

          <Button
            type="submit"
            data-testid="quote-submit"
            disabled={submitting}
            className="w-full rounded-full gradient-bg text-white font-semibold py-6 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_40px_rgba(229,36,49,0.45)] transition-all duration-300 btn-shine border-0"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Envoi…
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Envoyer ma demande
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
